import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import { worklogKeys } from '@/features/projects/queries';
import * as api from './api';
import type { AddInvoiceLineInput, InvoiceInput } from './types';

export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (params: api.InvoiceListParams) => [...invoiceKeys.lists(), params] as const,
  detail: (id: string | number) => [...invoiceKeys.all, 'detail', id] as const,
  billingOverlaps: () => [...invoiceKeys.all, 'billing-overlap'] as const,
  projectOptions: (company: string) => [...invoiceKeys.all, 'project-options', company] as const,
};

/** Issuing, reverting, paying, editing or deleting a client invoice changes
 *  which worklogs are billed (DEV13-77) — refresh every worklog summary and
 *  every invoice's overlap warning, not just this invoice. */
function invalidateWorklogBilling(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: worklogKeys.all });
  qc.invalidateQueries({ queryKey: invoiceKeys.billingOverlaps() });
}

/** Truthy id that isn't the `-1` "skip" sentinel used by optional lookups. */
function isResolvable(id: string | number): boolean {
  return id !== '' && id !== -1 && id !== '-1' && id != null;
}

export function useInvoices(params: api.InvoiceListParams) {
  return useQuery({
    queryKey: invoiceKeys.list(params),
    queryFn: () => api.listInvoices(params),
    placeholderData: keepPreviousData,
  });
}

export function useNextInvoiceNumber(enabled: boolean) {
  return useQuery({
    queryKey: [...invoiceKeys.all, 'next-number'] as const,
    queryFn: () => api.getNextInvoiceNumber(),
    enabled,
    staleTime: 0,
  });
}

/** Draft invoices only: time already billed by other invoices (DEV13-77).
 *  `scope` (projects + period) is in the key so an edited period refetches;
 *  the lifecycle mutations invalidate all of them (`invalidateWorklogBilling`). */
export function useInvoiceBillingOverlap(
  id: string | number,
  scope: { projects: number[]; from: string | null; to: string | null },
  enabled: boolean,
) {
  return useQuery({
    queryKey: [...invoiceKeys.billingOverlaps(), id, scope] as const,
    queryFn: () => api.getInvoiceBillingOverlap(id),
    enabled: enabled && isResolvable(id),
  });
}

/** The projects an invoice for `company` ('' = none) may link (ADR-102).
 *  Refetched each time the form mounts (`staleTime: 0`): project create/edit/
 *  delete don't invalidate this finance key, and it's a small list. */
export function useInvoiceProjectOptions(company: string) {
  return useQuery({
    queryKey: invoiceKeys.projectOptions(company),
    queryFn: () => api.getInvoiceProjectOptions(company),
    placeholderData: keepPreviousData,
    staleTime: 0,
  });
}

export function useInvoice(id: string | number) {
  return useQuery({
    queryKey: invoiceKeys.detail(id),
    queryFn: () => api.getInvoice(id),
    // Resolves by public_id (string, from the URL) or integer pk; the backend
    // accepts either (ADR-041).
    enabled: isResolvable(id),
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InvoiceInput) => api.createInvoice(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      toast.success('Invoice created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateInvoice(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<InvoiceInput>) => api.updateInvoice(id, input),
    onSuccess: (invoice) => {
      qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      qc.setQueryData(invoiceKeys.detail(id), invoice);
      invalidateWorklogBilling(qc);
      toast.success('Invoice updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.deleteInvoice(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      invalidateWorklogBilling(qc);
      toast.success('Invoice deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useAddInvoiceLine(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddInvoiceLineInput) => api.addInvoiceLine(id, input),
    onSuccess: (invoice) => {
      // The detail page keys by public_id (from the URL), but this hook may be
      // called with the integer pk — update both so the table refreshes live.
      qc.setQueryData(invoiceKeys.detail(id), invoice);
      qc.setQueryData(invoiceKeys.detail(invoice.public_id), invoice);
      qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      toast.success('Line added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateInvoiceLine(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      lineId,
      input,
    }: {
      lineId: number;
      input: { description?: string; quantity?: string; unit_rate?: string };
    }) => api.updateInvoiceLine(id, lineId, input),
    onSuccess: (invoice) => {
      qc.setQueryData(invoiceKeys.detail(id), invoice);
      qc.setQueryData(invoiceKeys.detail(invoice.public_id), invoice);
      qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      toast.success('Line updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useRemoveInvoiceLine(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: number) => api.removeInvoiceLine(id, lineId),
    onSuccess: (invoice) => {
      qc.setQueryData(invoiceKeys.detail(id), invoice);
      qc.setQueryData(invoiceKeys.detail(invoice.public_id), invoice);
      qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      toast.success('Line removed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useMoveInvoiceLine(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lineId, direction }: { lineId: number; direction: 'up' | 'down' }) =>
      api.moveInvoiceLine(id, lineId, direction),
    onSuccess: (invoice) => {
      qc.setQueryData(invoiceKeys.detail(id), invoice);
      qc.setQueryData(invoiceKeys.detail(invoice.public_id), invoice);
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useIssueInvoice(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.issueInvoice(id),
    onSuccess: (invoice) => {
      qc.setQueryData(invoiceKeys.detail(id), invoice);
      qc.setQueryData(invoiceKeys.detail(invoice.public_id), invoice);
      qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      invalidateWorklogBilling(qc);
      toast.success('Invoice issued');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useUploadInvoicePdfInserts(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (files: File[]) => api.uploadInvoicePdfInserts(id, files),
    onSuccess: (invoice) => {
      qc.setQueryData(invoiceKeys.detail(id), invoice);
      qc.setQueryData(invoiceKeys.detail(invoice.public_id), invoice);
      toast.success('PDF attached');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRemoveInvoicePdfInsert(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (insertId: number) => api.removeInvoicePdfInsert(id, insertId),
    onSuccess: (invoice) => {
      qc.setQueryData(invoiceKeys.detail(id), invoice);
      qc.setQueryData(invoiceKeys.detail(invoice.public_id), invoice);
      toast.success('Attachment removed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRevertInvoiceToDraft(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.revertInvoiceToDraft(id),
    onSuccess: (invoice) => {
      qc.setQueryData(invoiceKeys.detail(id), invoice);
      qc.setQueryData(invoiceKeys.detail(invoice.public_id), invoice);
      qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      invalidateWorklogBilling(qc);
      toast.success('Reverted to draft');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useMarkInvoicePaid(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.markInvoicePaid(id),
    onSuccess: (invoice) => {
      qc.setQueryData(invoiceKeys.detail(id), invoice);
      qc.setQueryData(invoiceKeys.detail(invoice.public_id), invoice);
      qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
      invalidateWorklogBilling(qc);
      toast.success('Invoice marked paid');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useGenerateInvoicePdf(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.generateInvoicePdf(id),
    // Silent: this runs implicitly behind View/Download PDF (no explicit
    // "Generate" step), so it shouldn't announce itself with a toast.
    onSuccess: (invoice) => {
      qc.setQueryData(invoiceKeys.detail(id), invoice);
      qc.setQueryData(invoiceKeys.detail(invoice.public_id), invoice);
      qc.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
